'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { usePathname } from 'next/navigation';
import { useAuthStore } from '@/lib/auth-store';
import { isPublicAuthRoute } from '@/lib/public-routes';
import { notificationsApi } from '@/lib/api';
import { useUiBootstrap } from '@/lib/ui-bootstrap';
import {
  canBecomeOrganizer,
  getEffectiveRole,
  isAdminRole,
  isJuryRole,
  isOrganizationAdminRole,
  isOrganizerRole,
} from '@/lib/rbac';
import { canAccessOrganizationDashboard } from '@/lib/organization-access';
import { resolveDashboardForUser } from '@/lib/role-dashboard';
import {
  Sparkles,
  Bell,
  User,
  LogOut,
  LayoutDashboard,
  Building2,
  Calendar,
  CalendarDays,
  BarChart3,
  BadgeCheck,
  MessageCircle,
  Users,
  QrCode,
  ChevronDown,
  FileText,
  HelpCircle,
  BookOpenCheck,
  Play,
  Plus,
  Shield,
  Gavel,
  Ticket,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { EventMegaMenu } from './EventMegaMenu';
import { DashboardSearch } from './DashboardSearch';
import { NotificationBellCenter } from './NotificationBellCenter';
import { hideOnScrollNavClass, useHideOnScroll } from '@/hooks/useHideOnScroll';
import {
  DOCUMENTATION_NAV_ITEMS,
  GUIDE_AUDIENCE_ORDER,
  PLATFORM_GUIDES,
} from '@/config/platform-guides';

interface AppNavbarProps {
  showCreateEvent?: boolean;
  showSearch?: boolean;
  layoutMode?: 'default' | 'dashboard';
  /** Whether page has a hero globe band header behind navbar */
  isHeroHeader?: boolean;
}

const DOCUMENTATION_ITEMS = [
  {
    name: 'Articles',
    description: DOCUMENTATION_NAV_ITEMS[0].description,
    href: DOCUMENTATION_NAV_ITEMS[0].href,
    icon: FileText,
  },
  {
    name: 'Help Center',
    description: DOCUMENTATION_NAV_ITEMS[1].description,
    href: DOCUMENTATION_NAV_ITEMS[1].href,
    icon: HelpCircle,
  },
];

const ORGANIZER_MENU_ITEMS = [
  { href: '/dashboard/calendar', label: 'Calendar', icon: Calendar },
  { href: '/dashboard/events', label: 'Events', icon: CalendarDays },
  { href: '/dashboard/endorsements', label: 'Pending Endorsements', icon: BadgeCheck },
  { href: '/dashboard/analytics', label: 'Analytics', icon: BarChart3 },
  { href: '/dashboard/passes', label: 'Multi-Event Passes', icon: BadgeCheck },
  { href: '/dashboard/my-passes', label: 'My Passes', icon: QrCode },
  { href: '/dashboard/support', label: 'Support Chat', icon: MessageCircle },
  { href: '/dashboard/teams', label: 'Teams', icon: Users },
];

const JURY_MENU_ITEMS = [
  { href: '/jury', label: 'Overview', icon: LayoutDashboard },
  { href: '/jury/assigned-events', label: 'Jury Events', icon: CalendarDays },
];

export function AppNavbar({
  showCreateEvent = true,
  showSearch: _showSearch = true,
  layoutMode = 'default',
  isHeroHeader = false,
}: AppNavbarProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { user, isAuthenticated, authChecking, logout } = useAuthStore();
  const { getMenu, canRender, trackClick } = useUiBootstrap();
  const effectiveRole = getEffectiveRole(user);
  const isPlatformAdmin = isAdminRole(effectiveRole);
  const isOrganizerUser = isOrganizerRole(effectiveRole);
  const isJury = isJuryRole(effectiveRole);
  const isOrgDashboardUser = canAccessOrganizationDashboard(user);
  const showBecomeOrganizer = canBecomeOrganizer(user);

  const dashboardHref = resolveDashboardForUser(user);
  const createEventHref = isOrganizerUser || isOrgDashboardUser ? '/dashboard/events/new' : '/become-organizer';

  const primaryDashboardLabel = isPlatformAdmin
    ? 'Admin panel'
    : isOrgDashboardUser
      ? 'Organization dashboard'
      : isOrganizerUser
        ? 'Dashboard'
        : isJury
          ? 'Overview'
          : 'Overview';

  const profileHref = isPlatformAdmin
    ? '/dashboard/admin/profile'
    : isOrganizationAdminRole(effectiveRole)
      ? '/dashboard/organization/profile'
      : isOrganizerUser || isOrgDashboardUser
        ? '/dashboard/profile'
        : isJury
          ? '/jury/profile'
          : '/dashboard/profile';
  const [eventsMenuOpen, setEventsMenuOpen] = useState(false);
  const [guidesMenuOpen, setGuidesMenuOpen] = useState(false);
  const [resourcesMenuOpen, setResourcesMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsError, setNotificationsError] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const anyMenuOpen = eventsMenuOpen || guidesMenuOpen || resourcesMenuOpen || userMenuOpen || notificationsOpen;
  const headerHidden = useHideOnScroll({ enabled: !anyMenuOpen });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setEventsMenuOpen(false);
        setGuidesMenuOpen(false);
        setResourcesMenuOpen(false);
        setUserMenuOpen(false);
        setNotificationsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const closeHoverMenus = () => {
    setEventsMenuOpen(false);
    setGuidesMenuOpen(false);
    setResourcesMenuOpen(false);
  };

  const handleLogout = async () => {
    // Await logout so HttpOnly cookies are cleared before navigation.
    await logout();
    setUserMenuOpen(false);
    // Full reload to clear all in-memory state
    window.location.href = '/login?reason=logged_out';
  };

  const isAuthed =
    !authChecking &&
    isAuthenticated &&
    Boolean(user?.id || user?.email) &&
    !isPublicAuthRoute(pathname);

  const toggleNotifications = () => {
    if (!isAuthed) return;
    setNotificationsOpen((open) => {
      const next = !open;
      if (next && !notifications.length && !notificationsLoading) {
        setNotificationsLoading(true);
        setNotificationsError(null);
        notificationsApi
          .list()
          .then((res) => {
            setNotifications(res.data?.data ?? []);
          })
          .catch(() => {
            setNotificationsError('Failed to load notifications');
            setNotifications([]);
          })
          .finally(() => {
            setNotificationsLoading(false);
          });
      }
      return next;
    });
  };
  const isHeroGlobePage =
    pathname === '/' ||
    pathname === '/host-events' ||
    pathname === '/pricing' ||
    Boolean(pathname?.startsWith('/guides')) ||
    pathname === '/articles' ||
    pathname === '/resources' ||
    pathname === '/developers';
  const isLandingPage = Boolean(isHeroHeader || isHeroGlobePage);

  useEffect(() => {
    if (layoutMode === 'dashboard') return;

    const onScroll = () => {
      setIsScrolled(window.scrollY > 8);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [layoutMode]);

  const navIsTransparent = isLandingPage && !isScrolled;

  return (
    <nav 
      className={`left-0 right-0 transition-all duration-300 ease-out ${hideOnScrollNavClass(headerHidden)} ${
        layoutMode === 'dashboard' 
          ? 'sticky top-0 z-40 bg-bg border-b border-nature-border/50' 
          : `fixed top-0 z-50 ${
              navIsTransparent
                ? 'bg-transparent border-b border-transparent shadow-none'
                : 'border-b border-[#E6E2D7] bg-[#F2EFE2]/90 shadow-[0_4px_24px_rgba(45,53,54,0.06)] backdrop-blur-xl'
            }`
      }`} 
      aria-hidden={headerHidden}
      onMouseLeave={closeHoverMenus}
    >
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8" onMouseLeave={closeHoverMenus}>
        <div className="flex items-center justify-between h-20">
          {layoutMode !== 'dashboard' && (
            <Link href="/" className="flex items-center gap-3 group shrink-0">
              <div className="relative h-16 w-40 flex items-center transition-transform duration-300 group-hover:scale-105 px-1">
                <Image src="/logo-eh.png" alt="EnthiosHub" fill className="object-contain drop-shadow-xs" priority />
              </div>
            </Link>
          )}

          <div className="hidden lg:flex flex-1 justify-center items-center">
            {layoutMode === 'dashboard' ? (
              <DashboardSearch />
            ) : (
              <>
                <div
                  className="relative"
                  onMouseEnter={() => setEventsMenuOpen(true)}
                  onMouseLeave={() => setEventsMenuOpen(false)}
                >
                  <button
                    className={`inline-flex h-10 items-center gap-1 whitespace-nowrap rounded-full px-3.5 text-[15px] font-medium leading-none transition-all duration-200 ${
                      eventsMenuOpen
                        ? 'bg-bg text-nature-text shadow-sm ring-1 ring-black/5'
                        : 'text-[#2D3536] hover:text-[#697C70] hover:bg-[#2D3536]/5'
                    }`}
                  >
                    Explore Events
                    <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${eventsMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {eventsMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="absolute left-1/2 top-[calc(100%+10px)] -translate-x-1/2 z-50"
                      >
                        <div className="overflow-hidden rounded-2xl border border-nature-border bg-white shadow-xl">
                          <EventMegaMenu onClose={() => setEventsMenuOpen(false)} />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <div className="ml-2 flex items-center gap-1">
                  {[
                    { name: 'Calendar', href: '/events/calendar' },
                    { name: 'Host Event', href: '/host-events' },
                    { name: 'Pricing', href: '/pricing' },
                  ].map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      className="inline-flex h-9 items-center whitespace-nowrap rounded-full px-3 text-[13px] font-medium transition-all duration-200 text-[#2D3536] hover:text-[#697C70] hover:bg-[#2D3536]/5"
                    >
                      {item.name}
                    </Link>
                  ))}

                  <div
                    className="relative"
                    onMouseEnter={() => setGuidesMenuOpen(true)}
                    onMouseLeave={() => setGuidesMenuOpen(false)}
                  >
                    <button
                      className={`inline-flex h-10 items-center gap-1 whitespace-nowrap rounded-full px-3.5 text-[15px] font-medium leading-none transition-all duration-200 ${
                        guidesMenuOpen
                          ? 'bg-bg text-nature-text shadow-sm ring-1 ring-black/5'
                          : 'text-[#2D3536] hover:text-[#697C70] hover:bg-[#2D3536]/5'
                      }`}
                    >
                      Event Guide
                      <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${guidesMenuOpen ? 'rotate-180' : ''}`} />
                    </button>

                    <AnimatePresence>
                      {guidesMenuOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 10, scale: 0.95 }}
                          transition={{ duration: 0.2, ease: 'easeOut' }}
                          className="absolute left-1/2 top-[calc(100%+10px)] w-[22rem] -translate-x-1/2 z-50"
                        >
                          <div className="overflow-hidden rounded-2xl border border-nature-border bg-bg/95 p-3 backdrop-blur-xl shadow-lg">
                            <p className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-nature-muted">
                              Step-by-step guides
                            </p>
                            <div className="grid grid-cols-1 gap-1">
                              {GUIDE_AUDIENCE_ORDER.map((audience) => {
                                const guide = PLATFORM_GUIDES[audience];
                                const GuideIcon = guide.icon;
                                return (
                                  <Link
                                    key={audience}
                                    href={`/guides/${audience}`}
                                    className="group/item flex items-start gap-3.5 rounded-xl p-3 transition-all hover:bg-nature-bg"
                                  >
                                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-nature-soft transition-colors group-hover/item:bg-primary/10 group-hover/item:text-primary">
                                      <GuideIcon className="h-4 w-4" />
                                    </div>
                                    <div className="min-w-0">
                                      <p className="text-xs font-bold text-nature-text group-hover/item:text-primary transition-colors">
                                        {guide.label}
                                      </p>
                                      <p className="mt-0.5 text-[10px] font-medium leading-relaxed text-nature-muted">
                                        {guide.navDescription}
                                      </p>
                                    </div>
                                  </Link>
                                );
                              })}
                            </div>

                            <div className="my-2 h-px bg-nature-border/70" />

                            <p className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-nature-muted">
                              Video walkthroughs
                            </p>
                            <div className="grid grid-cols-1 gap-1">
                              {GUIDE_AUDIENCE_ORDER.map((audience) => {
                                const guide = PLATFORM_GUIDES[audience];
                                return (
                                  <Link
                                    key={`${audience}-videos`}
                                    href={`/guides/${audience}#videos`}
                                    className="group/item flex items-start gap-3.5 rounded-xl p-3 transition-all hover:bg-nature-bg"
                                  >
                                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-nature-soft transition-colors group-hover/item:bg-primary/10 group-hover/item:text-primary">
                                      <Play className="h-4 w-4" />
                                    </div>
                                    <div className="min-w-0">
                                      <p className="text-xs font-bold text-nature-text group-hover/item:text-primary transition-colors">
                                        {guide.label} videos
                                      </p>
                                      <p className="mt-0.5 text-[10px] font-medium leading-relaxed text-nature-muted">
                                        Watch tutorials for this workflow
                                      </p>
                                    </div>
                                  </Link>
                                );
                              })}
                            </div>

                            <Link
                              href="/guides/organizers"
                              className="mt-2 flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold text-primary hover:bg-nature-bg"
                            >
                              <BookOpenCheck className="h-4 w-4" />
                              Open full Event Guide
                            </Link>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <div
                    className="relative"
                    onMouseEnter={() => setResourcesMenuOpen(true)}
                    onMouseLeave={() => setResourcesMenuOpen(false)}
                  >
                    <button
                      className={`inline-flex h-10 items-center gap-1 whitespace-nowrap rounded-full px-3.5 text-[15px] font-medium leading-none transition-all duration-200 ${
                        resourcesMenuOpen
                          ? 'bg-bg text-nature-text shadow-sm ring-1 ring-black/5'
                          : 'text-[#2D3536] hover:text-[#697C70] hover:bg-[#2D3536]/5'
                      }`}
                    >
                      Documentation
                      <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${resourcesMenuOpen ? 'rotate-180' : ''}`} />
                    </button>

                    <AnimatePresence>
                      {resourcesMenuOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 10, scale: 0.95 }}
                          transition={{ duration: 0.2, ease: 'easeOut' }}
                          className="absolute left-1/2 top-[calc(100%+10px)] w-80 -translate-x-1/2 z-50"
                        >
                          <div className="overflow-hidden rounded-2xl border border-nature-border bg-bg/95 p-2 backdrop-blur-xl shadow-lg">
                            <div className="grid grid-cols-1 gap-1">
                              {DOCUMENTATION_ITEMS.map((item) => {
                                const Icon = item.icon;
                                return (
                                  <Link
                                    key={item.name}
                                    href={item.href}
                                    className="group/item flex items-start gap-3.5 rounded-xl p-3 transition-all hover:bg-nature-bg"
                                  >
                                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-nature-soft transition-colors group-hover/item:bg-primary/10 group-hover/item:text-primary">
                                      <Icon className="h-4 w-4" />
                                    </div>
                                    <div className="min-w-0">
                                      <p className="text-xs font-bold text-nature-text group-hover/item:text-primary transition-colors">
                                        {item.name}
                                      </p>
                                      <p className="mt-0.5 text-[10px] font-medium leading-relaxed text-nature-muted">
                                        {item.description}
                                      </p>
                                    </div>
                                  </Link>
                                );
                              })}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex items-center gap-3">
            {isAuthed ? (
              <>
                <NotificationBellCenter />

                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setUserMenuOpen((o) => !o)}
                    className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full hover:bg-[#2D3536]/5 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-semibold text-primary overflow-hidden">
                      <User className="w-4 h-4" />
                    </div>
                    <span className="text-sm font-medium hidden md:inline-block max-w-[120px] truncate text-[#2D3536]">
                      {user?.name || user?.email || 'Account'}
                    </span>
                    <ChevronDown className="w-4 h-4 text-[#2D3536]/70" />
                  </button>

                  {userMenuOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        aria-hidden
                        onClick={() => setUserMenuOpen(false)}
                      />
                      <div className="absolute right-0 top-full mt-2 w-56 py-1.5 bg-bg border border-nature-border rounded-xl shadow-lg z-50">
                        <div className="px-4 py-2 border-b border-nature-border">
                          <p className="text-xs font-medium text-nature-muted">Signed in as</p>
                          <p className="text-sm font-semibold text-nature-text truncate">{user?.name || user?.email}</p>
                        </div>
                        <Link
                          href={dashboardHref}
                          className="flex items-center gap-2 px-4 py-2.5 text-sm text-nature-text hover:bg-nature-bg"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <LayoutDashboard className="w-4 h-4" />
                          {primaryDashboardLabel}
                        </Link>
                        <Link
                          href={profileHref}
                          className="flex items-center gap-2 px-4 py-2.5 text-sm text-nature-text hover:bg-nature-bg"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <User className="w-4 h-4" />
                          Profile
                        </Link>
                        {isPlatformAdmin && (
                          <Link
                            href="/dashboard/admin"
                            className="flex items-center gap-2 px-4 py-2.5 text-sm text-nature-text hover:bg-nature-bg"
                            onClick={() => setUserMenuOpen(false)}
                          >
                            <Shield className="w-4 h-4" />
                            Admin Console
                          </Link>
                        )}
                        <Link
                          href="/scanner"
                          className="flex items-center gap-2 px-4 py-2.5 text-sm text-nature-text hover:bg-nature-bg"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <QrCode className="w-4 h-4" />
                          Scanner
                        </Link>

                        <hr className="my-1.5 border-nature-border" />
                        <button
                          type="button"
                          onClick={handleLogout}
                          className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50/50"
                        >
                          <LogOut className="w-4 h-4" />
                          Sign out
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link
                  href="/login"
                  className="px-4 py-2 text-sm font-medium transition-colors text-[#2D3536] hover:text-[#697C70]"
                >
                  Sign In
                </Link>
                <Link
                  href="/register"
                  className="px-4 py-2 bg-[#697C70] hover:bg-[#5a6b5e] text-[#F2EFE2] rounded-lg text-xs sm:text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 shadow-xs"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      {notificationsOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            aria-hidden
            onClick={() => setNotificationsOpen(false)}
          />
          <div className="absolute right-4 top-full mt-2 w-80 max-h-96 overflow-auto py-2 bg-bg border border-nature-border rounded-[12px] shadow-lg z-50">
            <div className="flex items-center justify-between px-4 py-2">
              <p className="text-sm font-medium text-nature-text">Notifications</p>
              <Link
                href="/dashboard/notifications"
                className="text-xs text-primary hover:underline"
                onClick={() => setNotificationsOpen(false)}
              >
                View all
              </Link>
            </div>
            {notificationsLoading ? (
              <div className="px-4 py-6 text-center text-nature-muted text-sm">
                Loading notifications...
              </div>
            ) : notificationsError ? (
              <div className="px-4 py-6 text-center text-red-500 text-sm">
                {notificationsError}
              </div>
            ) : notifications.length === 0 ? (
              <p className="px-4 py-6 text-center text-nature-muted text-sm">No notifications yet</p>
            ) : (
              <ul className="divide-y divide-nature-border">
                {notifications.slice(0, 8).map((n) => (
                  <li
                    key={n.id}
                    className={`px-4 py-3 text-sm ${!n.read_at ? 'bg-nature-bg/60' : ''}`}
                  >
                    <p className="font-medium text-nature-text">
                      {n.title ?? 'Notification'}
                    </p>
                    {(n.message || n.body) && (
                      <p className="text-xs text-nature-muted mt-1">
                        {n.message ?? n.body}
                      </p>
                    )}
                    {n.created_at && (
                      <p className="text-[11px] text-nature-muted mt-1">
                        {n.created_at}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </nav>
  );
}
